import { useEffect, useMemo, useRef, useState } from 'react';
import { formatMoney, isNegativeNumber } from './utils/format';
import MetricCard from './components/MetricCard/MetricCard';

import { HISTORICAL_PRESETS } from './utils/period';

export const PERIODS = [
  { id: 'today', label: 'Today' },
  { id: 'this_week', label: 'This Week' },
  { id: 'this_month', label: 'This Month' },
];

export function ViewModeToggle({ mode, onChange }) {
  return (
    <div className="view-mode-toggle" role="tablist" aria-label="Data view mode">
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'live'}
        className={`view-mode-btn ${mode === 'live' ? 'view-mode-btn--active' : ''}`}
        onClick={() => onChange('live')}
      >
        Live (WebSocket)
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={mode === 'historical'}
        className={`view-mode-btn ${mode === 'historical' ? 'view-mode-btn--active' : ''}`}
        onClick={() => onChange('historical')}
      >
        Past dates
      </button>
    </div>
  );
}

export function PastDateFilter({
  from,
  to,
  onFrom,
  onTo,
  onApply,
  maxDate = undefined,
}) {
  const max = maxDate;
  return (
    <div className="past-date-filter">
      <div className="past-date-presets">
        {HISTORICAL_PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            className="period-btn past-preset-btn"
            onClick={() => {
              const r = p.range();
              onFrom(r.from);
              onTo(r.to);
            }}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="filter-custom">
        <span className="date-range-label">From</span>
        <input
          type="date"
          value={from}
          max={max}
          onChange={(e) => onFrom(e.target.value)}
        />
        <span>—</span>
        <span className="date-range-label">To</span>
        <input
          type="date"
          value={to}
          max={max}
          onChange={(e) => onTo(e.target.value)}
        />
        <button type="button" className="apply-btn apply-btn--primary" onClick={onApply}>
          Load data
        </button>
      </div>
      <p className="filter-hint filter-hint--historical">
        Closed P/L, commission, swap, deposits, and orders use the selected dates. Floating
        on STP is a live snapshot; WH floating uses accounts that traded in this range.
      </p>
    </div>
  );
}

export function PeriodRangeBanner({ closed, historical, inline = false }) {
  if (!closed?.date_from || !closed?.date_to) return null;
  const Tag = inline ? 'span' : 'p';
  const lag =
    typeof closed.period === 'string' && closed.period.includes('replica lag');
  const live =
    typeof closed.period === 'string' && closed.period.includes('live');
  return (
    <Tag className={`period-range-banner${historical ? ' period-range-banner--historical' : ''}${inline ? ' period-range-banner--inline' : ''}${lag ? ' period-range-banner--lag' : ''}${live ? ' period-range-banner--live' : ''}`}>
      {historical ? 'Historical view' : live ? 'Live deals' : lag ? 'Deals replica lag — showing' : 'Period'}:{' '}
      <strong>{closed.date_from}</strong>
      {' — '}
      <strong>{closed.date_to}</strong>
      {historical && ' (live WebSocket paused)'}
      {lag && ' (MySQL mt5_deals behind live)'}
      {live && ' (MT5 Web API)'}
    </Tag>
  );
}

/** Short date label for Closed PL / Deposit titles (YYYY-MM-DD or range). */
export function closedPeriodDateLabel(closed) {
  if (!closed?.date_from) return '';
  const from = String(closed.date_from).slice(0, 10);
  const to = String(closed.date_to || '').slice(0, 10);
  if (!to || to === from) return from;
  return `${from} — ${to}`;
}

export function DashboardLoadingBanner({ loading, label = 'Loading…', fullPage = false }) {
  if (!loading) return null;

  if (fullPage) {
    return (
      <div className="dashboard-loading-overlay" role="status" aria-live="polite">
        <div className="dashboard-loading-overlay__card">
          <span className="dashboard-loading-spinner dashboard-loading-spinner--lg" aria-hidden />
          <span className="dashboard-loading-overlay__label">{label}</span>
        </div>
      </div>
    );
  }

  return (
    <span className="dashboard-loading-banner" role="status" aria-live="polite">
      <span className="dashboard-loading-spinner" aria-hidden />
      {label}
    </span>
  );
}

export function KpiCard({ label, value, sub, large, numericValue, icon }) {
  return (
    <MetricCard
      label={label}
      value={value}
      sub={sub}
      large={large}
      numericValue={numericValue}
      icon={icon}
    />
  );
}

function rowMatchesFilters(row, columns, filters) {
  return columns.every((col) => {
    const q = (filters[col.key] || '').trim().toLowerCase();
    if (!q) return true;

    const display = String(row[col.key] ?? '').toLowerCase();
    const raw = row.__numeric?.[col.key];
    if (raw != null && String(raw).toLowerCase().includes(q)) return true;
    return display.includes(q);
  });
}

function numericSortValue(val) {
  if (val == null || val === '') return null;
  const n = Number(val);
  return Number.isFinite(n) ? n : null;
}

function compareRows(a, b, key, dir) {
  const aNum = numericSortValue(a.__numeric?.[key] ?? a[key]);
  const bNum = numericSortValue(b.__numeric?.[key] ?? b[key]);
  if (aNum != null && bNum != null) {
    const diff = aNum - bNum;
    return dir === 'asc' ? diff : -diff;
  }

  const aStr = String(a[key] ?? '');
  const bStr = String(b[key] ?? '');
  const cmp = aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? cmp : -cmp;
}

function FilterSearchIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16l5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

const tableSortState = new Map();

function getTableSortState(tableKey) {
  if (!tableSortState.has(tableKey)) {
    tableSortState.set(tableKey, { key: null, dir: 'asc' });
  }
  return tableSortState.get(tableKey);
}

export function DataTable({
  title,
  columns,
  rows,
  footer,
  blinkCells,
  enableColumnFilters = false,
  filterDebounceMs = 200,
  sortKey,
}) {
  const tableKey = sortKey || title || 'data-table';
  const [sort, setSort] = useState(() => ({ ...getTableSortState(tableKey) }));
  const [filters, setFilters] = useState({});
  const [draftFilters, setDraftFilters] = useState({});
  const [openFilters, setOpenFilters] = useState({});
  const [filterLoading, setFilterLoading] = useState(false);
  const inputRefs = useRef({});
  const filterDebounceRef = useRef(null);

  const hasActiveFilters = useMemo(
    () => Object.values(filters).some((v) => String(v).trim()),
    [filters]
  );

  const filteredRows = useMemo(
    () => rows.filter((row) => rowMatchesFilters(row, columns, filters)),
    [rows, columns, filters]
  );

  const sortedRows = useMemo(() => {
    if (!sort.key) return filteredRows;
    return [...filteredRows].sort((a, b) => compareRows(a, b, sort.key, sort.dir));
  }, [filteredRows, sort]);

  const handleSort = (key) => {
    setSort((prev) => {
      const next = {
        key,
        dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
      };
      tableSortState.set(tableKey, next);
      return next;
    });
  };

  const handleFilter = (key, value) => {
    setDraftFilters((prev) => ({ ...prev, [key]: value }));
    if (!enableColumnFilters) {
      setFilters((prev) => ({ ...prev, [key]: value }));
      return;
    }
    setFilterLoading(true);
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    filterDebounceRef.current = setTimeout(() => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setFilterLoading(false);
    }, filterDebounceMs);
  };

  useEffect(
    () => () => {
      if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    },
    []
  );

  const isFilterVisible = (key) =>
    openFilters[key] ||
    Boolean((filters[key] || draftFilters[key] || '').trim());

  const toggleFilterInput = (key) => {
    const visible = isFilterVisible(key);
    const hasText = Boolean((filters[key] || draftFilters[key] || '').trim());
    if (!visible) {
      setOpenFilters((prev) => ({ ...prev, [key]: true }));
      requestAnimationFrame(() => inputRefs.current[key]?.focus());
      return;
    }
    if (!hasText) {
      setOpenFilters((prev) => ({ ...prev, [key]: false }));
      return;
    }
    inputRefs.current[key]?.focus();
  };

  const clearAllFilters = () => {
    if (filterDebounceRef.current) clearTimeout(filterDebounceRef.current);
    setFilters({});
    setDraftFilters({});
    setOpenFilters({});
    setFilterLoading(false);
  };

  return (
    <div className="data-table-wrap">
      {title && (
        <div className="data-table-header">
          <h3 className="table-title">{title}</h3>
          <div className="data-table-header__actions">
            {enableColumnFilters && filterLoading && (
              <span className="data-table__filter-loading" role="status" aria-live="polite">
                <span className="data-table__filter-loading-spinner" aria-hidden />
                Searching…
              </span>
            )}
            {enableColumnFilters && hasActiveFilters && (
              <button type="button" className="data-table__clear-filters" onClick={clearAllFilters}>
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}
      <div className="data-table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => {
                const active = sort.key === col.key;
                const sortClass = active
                  ? ` data-table__sort-icon--${sort.dir}`
                  : ' data-table__sort-icon--idle';
                const isNum = col.className === 'num';
                const sortable = col.sortable !== false;
                const filterActive = Boolean(
                  (filters[col.key] || draftFilters[col.key] || '').trim()
                );
                const filterOpen = isFilterVisible(col.key);
                return (
                  <th
                    key={col.key}
                    className={`${col.className || ''}${filterOpen ? ' data-table__th--filter-open' : ''}`}
                  >
                    <div
                      className={`data-table__th-inner${isNum ? ' data-table__th-inner--num' : ''}`}
                    >
                      <div
                        className={`data-table__th-head${isNum ? ' data-table__th-head--num' : ''}`}
                      >
                        {sortable ? (
                          <button
                            type="button"
                            className={`data-table__sort${isNum ? ' data-table__sort--num' : ''}`}
                            onClick={() => handleSort(col.key)}
                            aria-sort={
                              active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'
                            }
                          >
                            <span>{col.label}</span>
                            <span className={`data-table__sort-icon${sortClass}`} aria-hidden="true" />
                          </button>
                        ) : (
                          <span className="data-table__th-label">{col.label}</span>
                        )}
                        {enableColumnFilters && col.filterable !== false && (
                          <button
                            type="button"
                            className={`data-table__filter-toggle${
                              filterActive ? ' data-table__filter-toggle--active' : ''
                            }${filterOpen ? ' data-table__filter-toggle--open' : ''}`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => toggleFilterInput(col.key)}
                            aria-label={`Filter ${col.label}`}
                            title={`Filter ${col.label}`}
                            aria-expanded={filterOpen}
                          >
                            <FilterSearchIcon />
                          </button>
                        )}
                      </div>
                      {enableColumnFilters && col.filterable !== false && filterOpen && (
                        <input
                          ref={(el) => {
                            if (el) inputRefs.current[col.key] = el;
                          }}
                          type="text"
                          className={`data-table__filter${isNum ? ' data-table__filter--num' : ''}${
                            filterLoading && (draftFilters[col.key] || '') !== (filters[col.key] || '')
                              ? ' data-table__filter--loading'
                              : ''
                          }`}
                          placeholder="Search…"
                          value={
                            enableColumnFilters
                              ? draftFilters[col.key] ?? filters[col.key] ?? ''
                              : filters[col.key] || ''
                          }
                          onChange={(e) => handleFilter(col.key, e.target.value)}
                          aria-label={`Filter ${col.label}`}
                          aria-busy={filterLoading}
                        />
                      )}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="empty-row">
                  No data for this period
                </td>
              </tr>
            ) : sortedRows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="empty-row">
                  No rows match the current filters
                </td>
              </tr>
            ) : (
              sortedRows.map((row, i) => (
                <tr key={row.id ?? i}>
                  {columns.map((col) => {
                    const cellId = row.id != null ? `${row.id}:${col.key}` : null;
                    const blink = cellId && blinkCells?.has(cellId);
                    const raw = row.__numeric?.[col.key];
                    const neg = raw != null && isNegativeNumber(raw);
                    return (
                      <td
                        key={col.key}
                        className={`${col.className || ''}${neg ? ' num--neg' : ''}${
                          blink ? ' cell--blink' : ''
                        }`.trim()}
                      >
                        {row[col.key]}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
          {footer && (!enableColumnFilters || !hasActiveFilters) && <tfoot>{footer}</tfoot>}
        </table>
      </div>
    </div>
  );
}

export function DateRangeRow({ from, to, onFrom, onTo, onApply, label }) {
  return (
    <div className="date-range-row">
      {label && <span className="date-range-label">{label}</span>}
      <input type="date" value={from} onChange={(e) => onFrom(e.target.value)} />
      <span>—</span>
      <input type="date" value={to} onChange={(e) => onTo(e.target.value)} />
      <button type="button" className="apply-btn" onClick={onApply}>
        Apply
      </button>
    </div>
  );
}

export function tableFooterTotal(colSpan, label, value) {
  return (
    <tr className="total-row">
      <td colSpan={colSpan}>{label}</td>
      <td className="num">{formatMoney(value)}</td>
    </tr>
  );
}
